import React, { useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import LoginPage from './components/auth/LoginPage';
import AdminDashboard from './components/dashboards/AdminDashboard';
import PatientDashboard from './components/dashboards/PatientDashboard';
import ProviderDashboard from './components/dashboards/ProviderDashboard';
import CommunityPortal from './components/portals/CommunityPortal';
import LMSPortal from './components/portals/LMSPortal';
import BlogPortal from './components/portals/BlogPortal';
import PublicPortal from './components/portals/PublicPortal';
import DealroomsPortal from './components/dealrooms/DealroomsPortal';
import DealroomDetail from './components/dealrooms/DealroomDetail';
import RewardsCenter from './components/rewards/RewardsCenter';
import ReferralManagement from './components/referrals/ReferralManagement';
import SubscriptionManagement from './components/subscriptions/SubscriptionManagement';
import ProfileSettings from './components/settings/ProfileSettings';
import AdvancedAnalytics from './components/analytics/AdvancedAnalytics';
import Navbar from './components/layout/Navbar';
import LoadingScreen from './components/common/LoadingScreen';
import PortalDashboard from './components/portals/PortalDashboard';
import SideNavigation from './components/layout/SideNavigation';
import ProviderDirectory from './components/providers/ProviderDirectory';
import ProviderClaims from './components/claims/ProviderClaims';
import TeamManagement from './components/team/TeamManagement';
import PortalCustomization from './components/customization/PortalCustomization';
import ImportCSV from './components/import/ImportCSV';
import AIChat from './components/ai/AIChat';
import AIIntegration from './components/ai/AIIntegration';
import ChatManagement from './components/chat/ChatManagement';
import CommunicationHub from './components/communication/CommunicationHub';
import Marketing from './components/marketing/Marketing';
import Promotions from './components/promotions/Promotions';
import Challenges from './components/challenges/Challenges';
import TeamChallenges from './components/challenges/TeamChallenges';
import PatientJourney from './components/patient/PatientJourney';
import UserRoleManagement from './components/users/UserRoleManagement';
import Footer from './components/layout/Footer';
import ReferralTracking from './components/referrals/ReferralTracking';
import ProductRecommendations from './components/recommendations/ProductRecommendations';
import PricingCards from './components/pricing/PricingCards';
import HelpHub from './components/help/HelpHub';
import TicketManagement from './components/tickets/TicketManagement';
import SuperAdminView from './components/superadmin/SuperAdminView';
import AutomationCenter from './components/automation/AutomationCenter';

const App = () => {
  const [currentPortal, setCurrentPortal] = useState('internal');
  const [isSideNavOpen, setIsSideNavOpen] = useState(true);

  return (
    <Router>
      <NotificationProvider>
        <MainContent
          currentPortal={currentPortal}
          setCurrentPortal={setCurrentPortal}
          isSideNavOpen={isSideNavOpen}
          setIsSideNavOpen={setIsSideNavOpen}
        />
      </NotificationProvider>
    </Router>
  );
};

const MainContent = ({ currentPortal, setCurrentPortal, isSideNavOpen, setIsSideNavOpen }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  const toggleSideNav = () => setIsSideNavOpen(!isSideNavOpen);

  return (
    <>
      {user ? (
        <div className="flex bg-gray-900 text-white min-h-screen">
          <SideNavigation currentPortal={currentPortal} isOpen={isSideNavOpen} />
          <div className="flex-1 flex flex-col min-w-0">
            <Navbar
              currentPortal={currentPortal}
              setCurrentPortal={setCurrentPortal}
              toggleSideNav={toggleSideNav}
              isSideNavOpen={isSideNavOpen}
            />
            <main className="flex-1 p-6 lg:p-8 overflow-y-auto">
              <Routes>
                <Route path="/" element={<Navigate to="/portal-dashboard" />} />
                <Route path="/portal-dashboard" element={<PortalDashboard />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/patient" element={<PatientDashboard />} />
                <Route path="/provider" element={<ProviderDashboard />} />
                <Route path="/community" element={<CommunityPortal />} />
                <Route path="/lms" element={<LMSPortal />} />
                <Route path="/blog" element={<BlogPortal />} />
                <Route path="/public" element={<PublicPortal />} />
                <Route path="/dealrooms" element={<DealroomsPortal />} />
                <Route path="/dealrooms/:dealId" element={<DealroomDetail />} />
                <Route path="/rewards" element={<RewardsCenter />} />
                <Route path="/referrals" element={<ReferralManagement />} />
                <Route path="/referral-tracking/:id" element={<ReferralTracking />} />
                <Route path="/subscriptions" element={<SubscriptionManagement />} />
                <Route path="/pricing" element={<PricingCards />} />
                <Route path="/settings" element={<ProfileSettings />} />
                <Route path="/analytics" element={<AdvancedAnalytics />} />
                <Route path="/provider-directory" element={<ProviderDirectory />} />
                <Route path="/claims" element={<ProviderClaims />} />
                <Route path="/team" element={<TeamManagement />} />
                <Route path="/portal-customization" element={<PortalCustomization />} />
                <Route path="/import-csv" element={<ImportCSV />} />
                <Route path="/ai-chat" element={<AIChat />} />
                <Route path="/ai-hub" element={<AIIntegration />} />
                <Route path="/chat" element={<ChatManagement />} />
                <Route path="/communications" element={<CommunicationHub />} />
                <Route path="/marketing" element={<Marketing />} />
                <Route path="/promotions" element={<Promotions />} />
                <Route path="/challenges" element={<Challenges />} />
                <Route path="/team-challenges" element={<TeamChallenges />} />
                <Route path="/patient-journey" element={<PatientJourney />} />
                <Route path="/user-roles" element={<UserRoleManagement />} />
                <Route path="/product-recommendations" element={<ProductRecommendations />} />
                <Route path="/help" element={<HelpHub />} />
                <Route path="/tickets" element={<TicketManagement />} />
                <Route path="/superadmin-view" element={<SuperAdminView />} />
                <Route path="/automation-center" element={<AutomationCenter />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </div>
      ) : (
        <Routes>
          <Route path="*" element={<LoginPage />} />
        </Routes>
      )}
    </>
  );
};

const Root = () => (
  <AuthProvider>
    <App />
  </AuthProvider>
);

export default Root;